import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SITE_CONFIG } from '../site.config';
import { GameBoard } from './components/GameBoard';
import { GameOverlay } from './components/GameOverlay';
import { ModePicker } from './components/ModePicker';
import { PiecePreview } from './components/PiecePreview';
import { SettingsDialog } from './components/SettingsDialog';
import { StatsDialog } from './components/StatsDialog';
import { TouchControls } from './components/TouchControls';
import { getGameMode } from './game/modes';
import type { GameModeId } from './game/types';
import { useGame } from './hooks/useGame';
import { useSettings } from './hooks/useSettings';
import { formatBinding, KEY_BINDING_DEFINITIONS } from './settings/settings';

function gameMessage(
  status: string,
  eventKind?: string,
  cleared = 0,
  contractMode = false,
  targetMet = false,
  lineTarget = 1,
): string {
  if (status === 'idle') return 'Ready to play.';
  if (status === 'paused') return 'Game paused.';
  if (status === 'gameOver') return 'Game over.';
  if (eventKind === 'lineClear') {
    if (contractMode) {
      return targetMet
        ? `Contract complete. Next target: ${lineTarget} line${lineTarget === 1 ? '' : 's'}.`
        : `${cleared} line${cleared === 1 ? '' : 's'} cleared. Target remains ${lineTarget}.`;
    }
    return `${cleared} line${cleared === 1 ? '' : 's'} cleared.`;
  }
  if (eventKind === 'hardDrop') return 'Hard drop.';
  if (eventKind === 'hold') return 'Piece held.';
  return 'Game in progress.';
}

function App() {
  const [restartOpen, setRestartOpen] = useState(false);
  const [restartMode, setRestartMode] = useState<GameModeId>('regular');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const pausedForRestart = useRef(false);
  const pausedForSettings = useRef(false);
  const pausedForStats = useRef(false);
  const cancelRestartButton = useRef<HTMLButtonElement>(null);
  const requestRestart = useCallback(() => setRestartOpen(true), []);
  const { settings, settingsControls } = useSettings();
  const { game, highScore, statsRevision, controls } = useGame(
    requestRestart,
    settings.keyBindings,
    !restartOpen && !settingsOpen && !statsOpen,
  );
  const mode = getGameMode(game.mode);

  const statusMessage = useMemo(() => gameMessage(
    game.status,
    game.lastEvent?.kind,
    game.lastEvent?.clearedRows.length,
    game.mode === 'contract',
    game.lastEvent?.targetMet,
    game.lineTarget,
  ), [game.lastEvent, game.lineTarget, game.mode, game.status]);

  useEffect(() => {
    document.documentElement.dataset.motion = settings.motionEffects ? 'on' : 'off';
    return () => { delete document.documentElement.dataset.motion; };
  }, [settings.motionEffects]);

  const handleRestart = () => {
    if (game.status === 'idle' || game.status === 'gameOver') controls.startNewGame();
    else setRestartOpen(true);
  };

  const confirmRestart = () => {
    pausedForRestart.current = false;
    setRestartOpen(false);
    controls.startNewGame(restartMode);
  };

  const cancelRestart = useCallback(() => {
    setRestartOpen(false);
    if (pausedForRestart.current && game.status === 'paused') controls.togglePause();
    pausedForRestart.current = false;
  }, [controls, game.status]);

  const closeSettings = useCallback(() => {
    setSettingsOpen(false);
    if (pausedForSettings.current && game.status === 'paused') controls.togglePause();
    pausedForSettings.current = false;
  }, [controls, game.status]);

  const closeStats = useCallback(() => {
    setStatsOpen(false);
    if (pausedForStats.current && game.status === 'paused') controls.togglePause();
    pausedForStats.current = false;
  }, [controls, game.status]);

  useEffect(() => {
    if (!restartOpen) return;
    setRestartMode(game.mode);
    if (game.status === 'playing') {
      pausedForRestart.current = true;
      controls.togglePause();
    }
    cancelRestartButton.current?.focus();

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        event.preventDefault();
        cancelRestart();
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [cancelRestart, controls, game.mode, game.status, restartOpen]);

  useEffect(() => {
    if (!settingsOpen || game.status !== 'playing') return;
    pausedForSettings.current = true;
    controls.togglePause();
  }, [controls, game.status, settingsOpen]);

  useEffect(() => {
    if (!statsOpen || game.status !== 'playing') return;
    pausedForStats.current = true;
    controls.togglePause();
  }, [controls, game.status, statsOpen]);

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href={import.meta.env.BASE_URL} aria-label="Codytris home">
          <span className="brand-mark" aria-hidden="true"><i /><i /><i /><i /></span>
          <span>
            <strong>{SITE_CONFIG.title}</strong>
            <small>{SITE_CONFIG.projectLabel}</small>
          </span>
        </a>
        <div className="topbar-status" aria-live="polite">
          <span className={`status-dot status-dot--${game.status}`} />
          {game.status === 'playing' ? `${mode.name} mode` : game.status === 'gameOver' ? 'Game over' : game.status}
        </div>
        <div className="topbar-actions">
          <button type="button" className="icon-action" onClick={() => setStatsOpen(true)} aria-label="Open locally saved statistics" title="Local stats — saved on this device">
            <span className="stats-icon" aria-hidden="true"><i /><i /><i /></span>
          </button>
          <button type="button" className="icon-action" onClick={() => setSettingsOpen(true)} aria-label="Open settings">
            <span aria-hidden="true">⚙</span>
          </button>
          <button
            type="button"
            className="icon-action"
            onClick={controls.togglePause}
            disabled={game.status === 'idle' || game.status === 'gameOver'}
            aria-label={game.status === 'paused' ? 'Resume game' : 'Pause game'}
          >
            <span aria-hidden="true">{game.status === 'paused' ? '▶' : 'Ⅱ'}</span>
          </button>
        </div>
      </header>

      <div className="game-status-line" aria-live="polite">
        <span className="mode-status">{game.status === 'idle' ? 'Select mode' : mode.name}</span>
        {game.mode === 'contract' && game.status !== 'idle' && <span className="contract-status">Target {game.lineTarget}</span>}
        <span>Status:</span> {statusMessage}
      </div>

      <main className="game-layout">
        <section className="panel hold-panel" aria-labelledby="hold-title">
          <div className="panel-heading">
            <span id="hold-title">Hold</span>
            <small>{!mode.holdEnabled ? 'LOCKED' : game.canHold ? 'READY' : 'USED'}</small>
          </div>
          <PiecePreview type={game.held} label="Held piece" />
          <p className="panel-note">{mode.holdEnabled ? 'Store one piece for later.' : `Hold is disabled in ${mode.name}.`}</p>
        </section>

        <section className="stats-panel" aria-label="Game statistics">
          <div className="stat"><span>Score</span><strong>{game.score.toLocaleString()}</strong></div>
          <div className="stat">
            <span>{game.mode === 'contract' ? 'Level / Target' : 'Level'}</span>
            <strong>{String(game.level).padStart(2, '0')}{game.mode === 'contract' && ` / ${game.lineTarget}`}</strong>
          </div>
          <div className="stat"><span>Lines</span><strong>{String(game.lines).padStart(2, '0')}</strong></div>
          <div className="stat stat--record"><span>{mode.name} best</span><strong>{highScore.toLocaleString()}</strong></div>
        </section>

        <section className="board-area" aria-label="Codytris game">
          <div className="board-stage">
            <GameBoard game={game} controls={controls} showGhost={settings.showGhost && mode.ghostEnabled} />
            <GameOverlay game={game} highScore={highScore} onStart={controls.startNewGame} onResume={controls.togglePause} />
            {game.lastEvent?.levelUp && (
              <div className="level-banner" key={game.lastEvent.id} aria-live="polite">Level {game.level}</div>
            )}
          </div>
          <div className="board-caption"><span>10 × 20</span><span>CODYTRIS</span></div>
        </section>

        <section className="panel next-panel" aria-labelledby="next-title">
          <div className="panel-heading">
            <span id="next-title">Up next</span>
            <small>{mode.previewCount > 0 ? 'PREVIEW' : 'HIDDEN'}</small>
          </div>
          <div className="next-list">
            {mode.previewCount === 0 && (
              <div className="next-hidden" aria-label="Next pieces are hidden in Fog mode">
                <span aria-hidden="true">?</span>
                <small>No signal</small>
              </div>
            )}
            {game.queue.slice(0, mode.previewCount).map((type, index) => (
              <div className="next-item" key={`${type}-${index}`}>
                <span className="next-index">{index + 1}</span>
                <PiecePreview type={type} label={`Next piece ${index + 1}`} compact={index > 0} />
              </div>
            ))}
          </div>
        </section>

        <section className="action-panel" aria-label="Game actions">
          <button
            type="button"
            className="secondary-button"
            onClick={controls.togglePause}
            disabled={game.status === 'idle' || game.status === 'gameOver'}
          >
            <span>{game.status === 'paused' ? 'Resume game' : 'Pause game'}</span><kbd>{formatBinding(settings.keyBindings.pause)}</kbd>
          </button>
          <button type="button" className="secondary-button secondary-button--danger" onClick={handleRestart}>
            <span>Restart</span><kbd>{formatBinding(settings.keyBindings.restart)}</kbd>
          </button>
        </section>

        <section className="panel keyboard-panel" aria-labelledby="controls-title">
          <div className="panel-heading"><span id="controls-title">Keybinds</span><small>CUSTOMIZABLE</small></div>
          <dl className="key-list">
            {KEY_BINDING_DEFINITIONS.map(({ action, label }) => (
              <div key={action}>
                <dt><kbd>{formatBinding(settings.keyBindings[action])}</kbd></dt>
                <dd>{label}</dd>
              </div>
            ))}
          </dl>
          <button type="button" className="panel-settings-link" onClick={() => setSettingsOpen(true)}>Change controls</button>
        </section>
      </main>

      <TouchControls controls={controls} paused={game.status === 'paused'} holdDisabled={!mode.holdEnabled} haptics={settings.touchHaptics} />

      <footer className="site-footer">
        <span><strong>Codytris</strong> by @{SITE_CONFIG.githubOwner}</span>
        <span className="footer-links">
          <a href={`${import.meta.env.BASE_URL}LICENSE.txt`} aria-label="MIT license">MIT License</a>
          {SITE_CONFIG.repositoryUrl && <a href={SITE_CONFIG.repositoryUrl} target="_blank" rel="noreferrer">GitHub ↗</a>}
        </span>
      </footer>

      {restartOpen && (
        <div className="dialog-backdrop" role="presentation" onMouseDown={cancelRestart}>
          <section className="confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="restart-title" onMouseDown={(event) => event.stopPropagation()}>
            <span className="overlay-kicker">Restart</span>
            <h2 id="restart-title">Restart this game?</h2>
            <p>Your current score and stack will disappear. Your personal best is staying right where it is.</p>
            <ModePicker legend="Restart in" selectedMode={restartMode} onSelect={setRestartMode} />
            <div className="dialog-actions">
              <button ref={cancelRestartButton} type="button" className="secondary-button" onClick={cancelRestart}>Keep playing</button>
              <button type="button" className="primary-button primary-button--danger" onClick={confirmRestart}>Restart</button>
            </div>
          </section>
        </div>
      )}

      {settingsOpen && <SettingsDialog settings={settings} controls={settingsControls} onClose={closeSettings} />}
      {statsOpen && <StatsDialog revision={statsRevision} onClose={closeStats} />}
    </div>
  );
}

export default App;
